import { CountryCode } from '../saleor-graphql';
import { IChannel } from '../channels/types';
import { countries } from 'country-data-list';
import * as ct from 'countries-and-timezones';

// Type the countries object
const typedCountries = countries as unknown as Record<string, {
  name: string;
  emoji: string;
  alpha2: string;
  alpha3: string;
  [key: string]: any;
}>;

/**
 * Converts ISO country code to flag emoji
 * @param isoCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'IN')
 * @returns Flag emoji string
 */
export function countryCodeToFlag(isoCode: string): string {
  const code = isoCode.toUpperCase();
  
  // Special cases
  if (code === 'UK') return '🇬🇧'; // UK is commonly used but should be GB
  
  return typedCountries[code]?.emoji || code;
}

/**
 * Gets the country name from ISO code
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Country name or the code itself if not found
 */
export function getCountryName(countryCode: CountryCode | string): string {
  const code = countryCode.toUpperCase();
  return typedCountries[code]?.name || countryCode;
}

/**
 * Country data with code, name, and flag emoji
 */
export interface CountryData {
  code: CountryCode;
  name: string;
  flag: string;
}

/**
 * Gets complete country data including name and flag
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Country data object
 */
export function getCountryData(countryCode: CountryCode | string): CountryData {
  const code = countryCode.toUpperCase();
  const country = typedCountries[code];
  
  return {
    code: countryCode as CountryCode,
    name: country?.name || countryCode,
    flag: country?.emoji || countryCode,
  };
}

/**
 * Gets country data for multiple country codes
 * @param countryCodes - Array of ISO country codes
 * @returns Array of country data objects
 */
export function getCountriesData(countryCodes: (CountryCode | string)[]): CountryData[] {
  return countryCodes.map(code => getCountryData(code));
}

/**
 * Gets ISO code from country name
 * @param countryName - Country name to search for
 * @returns ISO 3166-1 alpha-2 country code or null if not found
 */
export function getCountryCodeFromName(countryName: string): CountryCode | null {
  const normalizedName = countryName.trim().toLowerCase();
  
  // Search through countries
  for (const [code, data] of Object.entries(typedCountries)) {
    if (data.name?.toLowerCase() === normalizedName) {
      return code as CountryCode;
    }
  }
  
  return null;
}

/**
 * Extracts supported countries from channel configuration
 * @param channels - The channel configuration object
 * @returns Array of supported country codes
 */
export function getSupportedCountriesFromChannels(channels: Record<string, IChannel>): CountryCode[] {
  const countries = new Set<CountryCode>();
  
  for (const channel of Object.values(channels)) {
    // Add default country
    countries.add(channel.defaultCountry);
    
    // Add supported countries if they exist
    if (channel.supportedCountries) {
      channel.supportedCountries.forEach(country => countries.add(country));
    }
  }
  
  return Array.from(countries);
}


/**
 * Detects the user's country based on their browser timezone
 * Does not require location permissions
 */
export function detectCountryFromTimezone(): CountryCode | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (!timezone) {
      return null;
    }

    // Get timezone info from countries-and-timezones
    const timezoneInfo = ct.getTimezone(timezone);
    
    if (timezoneInfo && timezoneInfo.countries.length > 0) {
      // Return the first country code (most timezones belong to one country)
      return timezoneInfo.countries[0] as CountryCode;
    }

    return null;
  } catch (error) {
    console.error('Error detecting country from timezone:', error);
    return null;
  }
}


/**
 * Maps a country code to the appropriate channel slug based on the provided channel configuration
 * @param country - The country code to map
 * @param channels - The channel configuration object
 * @returns The channel slug for the given country
 */
export function getChannelForCountry(
  country: CountryCode, 
  channels: Record<string, IChannel>
): string {
  // Find a channel that has this country as its default
  for (const channel of Object.values(channels)) {
    if (channel.defaultCountry === country) {
      return channel.slug;
    }
  }
  
  // Check if country is in any channel's supported countries
  for (const channel of Object.values(channels)) {
    if (channel.supportedCountries?.includes(country)) {
      return channel.slug;
    }
  }
  
  // Fall back to international channel or first channel with supportedCountries
  const intlChannel = Object.values(channels).find(ch => 
    ch.urlSlug === 'intl' || ch.supportedCountries?.length
  );
  
  return intlChannel?.slug || Object.values(channels)[0].slug;
}